import type { DirectoryPublic } from '@repo/types/directory';
import type { ReactNode } from 'react';

import { useDraggable, useDroppable } from '@dnd-kit/react';
import { useMemo } from 'react';

import { createDirectoryDragData, useFsDragDropContext } from '@/components/features/files/dnd';
import { canDropIntoDirectory, FS_DRAG_TYPE } from '@/hooks/fs/useFsDragDrop';
import { useFilesStorage } from '@/store/files-store';

interface DraggableDroppableDirectoryProps {
  directory: DirectoryPublic;
  children: ReactNode;
}

/**
 * Wrapper component that makes a directory both draggable and droppable.
 * - Draggable: Only enabled if the user has edit permission on the directory.
 * - Droppable: Only accepts drops if the user has write permission on the directory.
 * Shows isDragging opacity for all directories in the dragged selection group.
 */
export function DraggableDroppableDirectory(draggableDroppableDirectoryProps: DraggableDroppableDirectoryProps) {
  const { directory, children } = draggableDroppableDirectoryProps;
  const { isDragging: isContextDragging, draggedItem } = useFsDragDropContext();
  const { selected } = useFilesStorage();

  const canDrag = directory.permissions.edit;
  const canAcceptDrop = directory.permissions.write;

  // Determine if current drop is valid based on the dragged item
  const isValidDropTarget = canAcceptDrop && draggedItem !== null && canDropIntoDirectory(directory, draggedItem.entity, draggedItem.type);

  const { ref: draggableRef, isDragging: isThisItemDragging } = useDraggable({
    id: `directory-drag-${directory.id}`,
    disabled: !canDrag,
    data: createDirectoryDragData(directory),
  });

  const { ref: droppableRef, isDropTarget } = useDroppable({
    id: `directory-drop-${directory.id}`,
    disabled: !canAcceptDrop,
    data: { directory },
    // Only accept files and directories that aren't this directory
    accept: (source) => {
      const sourceData = source.data as
        | {
            type: string;
            entity: { id: string; permissions: { edit: boolean } };
          }
        | undefined;

      if (!sourceData) return false;

      // Must have edit permission to move the item
      if (!sourceData.entity.permissions.edit) return false;

      // Cannot drop a directory into itself
      if (sourceData.type === FS_DRAG_TYPE.DIRECTORY && sourceData.entity.id === directory.id) {
        return false;
      }

      return true;
    },
  });

  // Check if this directory is part of the dragged selection group
  const isPartOfDraggedSelection = useMemo(() => {
    if (!isContextDragging || !draggedItem) return false;

    // Check if the dragged item is part of the selection
    const isDraggedItemSelected =
      draggedItem.type === FS_DRAG_TYPE.FILE ? selected.files.some((f) => f.id === draggedItem.entity.id) : selected.directories.some((d) => d.id === draggedItem.entity.id);

    // If dragged item is selected, check if this directory is also in the selection
    if (isDraggedItemSelected) {
      return selected.directories.some((d) => d.id === directory.id);
    }

    return false;
  }, [isContextDragging, draggedItem, selected, directory.id]);

  // Show dragging opacity if this item is being dragged OR is part of the dragged selection
  const showDraggingOpacity = isThisItemDragging || isPartOfDraggedSelection;

  // Combine refs for both draggable and droppable
  const combinedRef = (element: HTMLDivElement | null) => {
    draggableRef(element);
    droppableRef(element);
  };

  return (
    <div
      ref={combinedRef}
      style={{
        opacity: showDraggingOpacity ? 0.5 : 1,
        cursor: canDrag ? 'grab' : 'default',
      }}
      className={isDropTarget && isValidDropTarget ? 'ring-focus-ring rounded-md ring-2 ring-offset-2' : undefined}
      data-drop-target={isDropTarget && isValidDropTarget ? 'true' : undefined}
    >
      {children}
    </div>
  );
}
